import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { Button, ButtonAppearance, ButtonSize, Heading, Text, useDeviceSize } from '@atlassian/learning-components';

import { UiFullWidthImageBlock } from 'src/graphql/generated/graphql';

export const FullWidthImageBlock: React.FC<{ contentBlock: UiFullWidthImageBlock }> = ({ contentBlock }) => {
  const {
    title,
    textContent,
    image,
    imagePosition: imageOnLeft = true,
    cta,
    mobileBackgroundImage,
    backgroundImage,
    backgroundColor,
    titleColor,
    textContentColor,
    ctaTextColor,
    ctaBackgroundColor,
    maxContentWidth = 1020,
    hideImageOnMobile = true,
  } = contentBlock;

  const deviceWidth = useDeviceSize();
  const isMobile = deviceWidth < 1024;

  const backgroundImageUrl = isMobile ? mobileBackgroundImage?.url : backgroundImage?.url;

  const shouldShowImage = (hideImageOnMobile ? !isMobile : true) && Boolean(image?.url);

  return (
    <div
      css={containerStyles}
      style={{
        backgroundColor: backgroundColor || 'transparent',
        backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
        flexDirection: isMobile ? 'column-reverse' : 'row',
      }}
      data-testid="full-width-image-block"
    >
      <div
        css={contentWrapperStyles}
        style={{
          maxWidth: `${maxContentWidth}px`,
          flexDirection: imageOnLeft ? 'row-reverse' : 'row',
        }}
      >
        <div css={textColumnStyles}>
          {title && (
            <div data-testid="full-width-image-block-title">
              <Heading level={2} color={titleColor}>
                {title}
              </Heading>
            </div>
          )}
          {textContent && (
            <div data-testid="full-width-image-block-text">
              <Text size="large" color={textContentColor}>
                {textContent}
              </Text>
            </div>
          )}
          {cta && (
            <div
              css={css({
                '& a:not(:hover)': {
                  backgroundColor: ctaBackgroundColor,
                },
                '& a:not(:hover) span': {
                  color: ctaTextColor,
                },
              })}
              data-testid="full-width-image-block-cta"
            >
              <Button
                label={cta.label}
                href={cta.link}
                appearance={ButtonAppearance.PRIMARY}
                size={ButtonSize.LARGE}
                isResponsive={true}
              />
            </div>
          )}
        </div>
        {shouldShowImage && (
          <div data-testid="full-width-image-block-image">
            <img src={image?.url} alt={title || 'presentation'} css={imageStyles} />
          </div>
        )}
      </div>
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: `${token('space.500')} ${token('space.200')}`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',

  [media.below.md]: {
    width: `calc(100% + ${token('space.400')})`,
    marginLeft: '-16px', // can't use margin with tokens
  },

  [media.above.sm]: {
    padding: `${token('space.1000')} ${token('space.200')}`,
  },
});

const contentWrapperStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  columnGap: token('space.1000'),

  [media.above.md]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

const textColumnStyles = css({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minWidth: 0,
  rowGap: token('space.500'),
});

const imageStyles = css({
  width: '100%',
  height: 'auto',
  maxWidth: '100%',
  objectFit: 'contain',
});
